import {
  createPaymentRecorderRequest,
  createContractRequest,
  fetchContractRequest,
  updateContractRequest,
  fetchContractsRequest,
  fetchCustomerContractsRequest,
  createPaymentIntentRequest
} from "../api/index";
import { handleErrorRequest } from "../helpers/api";
import _ from "lodash";
import { createActions, handleActions } from "redux-actions";
import { loadingMutation } from "../helpers/redux";
import { createSelector } from "reselect";

const getValueFromStore = value =>
  createSelector(
    state => state.contracts,
    state => {
      return state[value];
    }
  );

export const selectors = {
  isLoadingCreatePayment: getValueFromStore("loadingCreatePayment"),
  getFailureCreatePayment: getValueFromStore("failureCreatePayment"),
  isSuccessCreatePayment: getValueFromStore("successCreatePayment")
};

const identity = name => payload => ({ [name]: payload });
const failIdentity = identity("failMessage");

const createdActions = createActions({
  contracts: {
    PAYMENT_RECORDER_ATTEMPT: null,
    PAYMENT_RECORDER_SUCCESS: null,
    PAYMENT_RECORDER_FAILURE: failIdentity,
    SUBMIT_CONTRACT_ATTEMPT: null,
    SUBMIT_CONTRACT_SUCCESS: null,
    SUBMIT_CONTRACT_FAILURE: failIdentity,
    FETCH_CONTRACT_ATTEMPT: null,
    FETCH_CONTRACT_SUCCESS: null,
    FETCH_CONTRACT_FAILURE: failIdentity,
    FETCH_CONTRACTS_ATTEMPT: null,
    FETCH_CONTRACTS_SUCCESS: null,
    FETCH_CONTRACTS_FAILURE: failIdentity,
    SET_CURRENT_CONTRACT: identity("currentContract"),
    SET_CONTRACTS: identity("contracts"),
    SET_CURRENT_PAGE: identity("currentPage"),
    SET_TOTAL_PAGES: identity("totalPages"),
    SET_PER_PAGE: identity("perPage"),
    SET_SEARCH_SNAPSHOT: identity("searchSnapshot"),
    RESET_STATE: null,
    SET_PAYMENT_INTENT: identity("paymentIntent"),
    SUBMIT_PAYMENT_INTENT_FAILURE: identity("failMessage"),
    SUBMIT_PAYMENT_INTENT_ATTEMPT: null,
    SUBMIT_PAYMENT_INTENT_SUCCESS: null
  }
});

const {
  paymentRecorderAttempt,
  paymentRecorderSuccess,
  paymentRecorderFailure,
  submitContractAttempt,
  submitContractSuccess,
  submitContractFailure,
  fetchContractAttempt,
  fetchContractSuccess,
  fetchContractFailure,
  fetchContractsAttempt,
  fetchContractsSuccess,
  fetchContractsFailure,
  setCurrentContract,
  setTotalPages,
  setContracts,
  setPaymentIntent,
  submitPaymentIntentAttempt,
  submitPaymentIntentFailure,
  submitPaymentIntentSuccess
} = createdActions.contracts;

const createRecorderPayment = ({ dealer_id, contract_id, data }) => (
  dispatch,
  getState
) => {
  dispatch(paymentRecorderAttempt());
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return createPaymentRecorderRequest({
    dealer_id,
    contract_id,
    data,
    jwt
  }).then(({ response, error }) => {
    if (response) {
      dispatch(paymentRecorderSuccess());
      dispatch(setCurrentContract(response.data));
      return Promise.resolve({ response: response.data });
    } else {
      dispatch(paymentRecorderFailure(handleErrorRequest(error)));
      return Promise.resolve({ error });
    }
  });
};

const createContract = ({ dealer_id, data }) => (dispatch, getState) => {
  dispatch(submitContractAttempt());
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return createContractRequest({
    data: { contract: data },
    dealer_id,
    jwt
  }).then(({ response, error }) => {
    if (response) {
      dispatch(submitContractSuccess());
      return Promise.resolve(response.data);
    } else {
      dispatch(submitContractFailure(handleErrorRequest(error)));
      return Promise.reject(error);
    }
  });
};

const fetchContract = ({ dealer_id, contract_id }) => (dispatch, getState) => {
  dispatch(fetchContractAttempt());
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return fetchContractRequest({ contract_id, dealer_id, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(fetchContractSuccess());
        dispatch(setCurrentContract(response.data));
        return Promise.resolve(response.data);
      } else {
        dispatch(fetchContractFailure(handleErrorRequest(error)));
        return Promise.reject(error);
      }
    }
  );
};

const fetchContractSeamless = ({ dealer_id, contract_id }) => (
  dispatch,
  getState
) => {
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return fetchContractRequest({ contract_id, dealer_id, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(setCurrentContract(response.data));
        return Promise.resolve(response.data);
      } else {
        return Promise.reject(error);
      }
    }
  );
};

const fetchContracts = ({ dealer_id, query_string = "" }) => (
  dispatch,
  getState
) => {
  dispatch(fetchContractsAttempt());
  dispatch(setContracts(null));
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return fetchContractsRequest({ dealer_id, query_string, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(fetchContractsSuccess());
        dispatch(setContracts(response.data));
        dispatch(
          setTotalPages(parseInt(response.headers["x-total-pages"], 10))
        );
        return Promise.resolve(response.data);
      } else {
        dispatch(fetchContractsFailure(handleErrorRequest(error)));
        return Promise.reject(error);
      }
    }
  );
};

const fetchContractsWithContact = () => (dispatch, getState) => {
  dispatch(fetchContractsAttempt());
  let user = _.get(getState(), "auth.user", "");
  if (user.contact_for && user.contact_for.length > 0) {
    dispatch(fetchContractsSuccess());
    dispatch(setContracts(user.contact_for));
    dispatch(setTotalPages(10));
  }
};

const fetchContractsSeamless = ({ dealer_id, query_string = "" }) => (
  dispatch,
  getState
) => {
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return fetchContractsRequest({ dealer_id, query_string, jwt }).then(
    ({ response, error }) => {
      if (response) {
        dispatch(fetchContractsSuccess());
        dispatch(setContracts(response.data));
        dispatch(
          setTotalPages(parseInt(response.headers["x-total-pages"], 10))
        );
        return Promise.resolve(response.data);
      } else {
        return Promise.reject(error);
      }
    }
  );
};

const fetchCustomerContracts = ({ customer_id, dealer_id }) => async (
  dispatch,
  getState
) => {
  dispatch(fetchContractsAttempt());
  dispatch(setContracts(null));
  let jwt = _.get(getState(), "auth.user.jwt", "");
  const { response, error } = await fetchCustomerContractsRequest({
    dealer_id,
    customer_id,
    jwt
  });
  if (response) {
    dispatch(fetchContractsSuccess());
    dispatch(setContracts(response.data));
  } else {
    dispatch(fetchContractsFailure(handleErrorRequest(error)));
  }
};

const updateContract = ({ dealer_id, contract_id, data }) => (
  dispatch,
  getState
) => {
  dispatch(submitContractAttempt());
  let jwt = _.get(getState(), "auth.user.jwt", "");
  return updateContractRequest({
    dealer_id,
    contract_id,
    data: { contract: data },
    jwt
  }).then(({ response, error }) => {
    if (response) {
      const contract = response.data;
      const nextContracts = Object.create(
        _.get(getState(), "contracts.contracts", [])
      );
      const index = _.findIndex(nextContracts, { id: contract.id });
      if (index !== -1) {
        nextContracts.splice(index, 1, contract);
        dispatch(setContracts(nextContracts));
      }
      dispatch(submitContractSuccess());
      dispatch(setCurrentContract(null));
      return Promise.resolve(response.data);
    } else {
      dispatch(submitContractFailure(handleErrorRequest(error)));
      return Promise.reject(error);
    }
  });
};

const createPaymentIntent = (dealer_id, contract, data) => (
  dispatch,
  getState
) => {
  const jwt = _.get(getState(), "auth.user.jwt", "");
  dispatch(submitPaymentIntentAttempt());
  return createPaymentIntentRequest({
    dealer_id, contract, jwt,
    data,
  }).then(({ response, error }) => {
    if (response) {
      dispatch(submitPaymentIntentSuccess());
      dispatch(setPaymentIntent(response.data));
      return Promise.resolve(response.data);
    }
    dispatch(submitPaymentIntentFailure(handleErrorRequest(error)));
    return Promise.resolve({ error });
  });
};


export const initialState = {
  submittingContract: false,
  failSubmitContract: null,
  successSubmitContract: false,
  loadingSubmitPayment: false,
  failureSubmitPayment: null,
  successSubmitPayment: false,
  fetchingContract: false,
  failFetchContract: null,
  successFetchContract: false,
  currentContract: null,
  fetchingContracts: false,
  failFetchContracts: null,
  successFetchContracts: false,
  contracts: null,
  currentPage: 1,
  totalPages: 1,
  perPage:100,
  searchSnapshot: {
    tracking_number: "",
    from_date: "",
    to_date: "",
    customer: "",
    aasm_state: "",
    key_tag: ""
  }
};

export const actions = {
  ...createdActions.contracts,
  createContract,
  fetchContract,
  updateContract,
  fetchContracts,
  fetchCustomerContracts,
  fetchContractsSeamless,
  fetchContractSeamless,
  createRecorderPayment,
  createPaymentIntent,
  fetchContractsWithContact
};

const simplePayloadMutation = (state, { payload }) => ({
  ...state,
  ...payload
});

export default handleActions(
  {
    contracts: {
      PAYMENT_RECORDER_ATTEMPT: loadingMutation({
        name: "CreatePayment",
        type: "loading"
      }),
      PAYMENT_RECORDER_SUCCESS: loadingMutation({
        name: "CreatePayment",
        type: "success"
      }),
      PAYMENT_RECORDER_FAILURE: loadingMutation({
        name: "CreatePayment",
        type: "failure"
      }),
      SUBMIT_CONTRACT_ATTEMPT: (state, action) => ({
        ...state,
        submittingContract: true,
        failSubmitContract: null,
        successSubmitContract: false
      }),
      SUBMIT_CONTRACT_SUCCESS: (state, action) => ({
        ...state,
        submittingContract: false,
        failSubmitContract: null,
        successSubmitContract: true
      }),
      SUBMIT_CONTRACT_FAILURE: (state, { payload: { failMessage } }) => ({
        ...state,
        submittingContract: false,
        failSubmitContract: failMessage,
        successSubmitContract: false
      }),
      FETCH_CONTRACT_ATTEMPT: (state, action) => ({
        ...state,
        fetchingContract: true,
        failFetchContract: null,
        successFetchContract: false
      }),
      FETCH_CONTRACT_SUCCESS: (state, action) => ({
        ...state,
        fetchingContract: false,
        failFetchContract: null,
        successFetchContract: true
      }),
      FETCH_CONTRACT_FAILURE: (state, { payload: { failMessage } }) => ({
        ...state,
        fetchingContract: false,
        failFetchContract: failMessage,
        successFetchContract: false
      }),
      FETCH_CONTRACTS_ATTEMPT: (state, action) => ({
        ...state,
        fetchingContracts: true,
        failFetchContracts: null,
        successFetchContracts: false
      }),
      FETCH_CONTRACTS_SUCCESS: (state, action) => ({
        ...state,
        fetchingContracts: false,
        failFetchContracts: null,
        successFetchContracts: true
      }),
      FETCH_CONTRACTS_FAILURE: (state, { payload: { failMessage } }) => ({
        ...state,
        fetchingContracts: false,
        failFetchContracts: failMessage,
        successFetchContracts: false
      }),
      SET_CURRENT_CONTRACT: simplePayloadMutation,
      SET_CONTRACTS: simplePayloadMutation,
      SET_CURRENT_PAGE: simplePayloadMutation,
      SET_TOTAL_PAGES: simplePayloadMutation,
      SET_PER_PAGE: simplePayloadMutation,
      SET_SEARCH_SNAPSHOT: simplePayloadMutation,
      RESET_STATE: (state, action) => ({ ...state, ...initialState })
    }
  },
  initialState
);
